import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IonContent } from "@ionic/react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "components/common/ui/LoadingSpinner/LoadingSpinner";
import Footer from "components/footer/Footer";
import { useSelector } from "redux/hooks";
import { wordPressApi } from "app/api/wordpress/wordPressApi";
import "./CheckoutSuccessPage.css";

interface Product {
  name: string;
  quantity: number;
  price_inc_tax: number | string;
  base_price?: number | string;
  product_options?: Array<{ display_name: string; display_value: string }>;
  image_url?: string;
}

interface OrderData {
  orderId: number;
  billingAddress: { first_name: string; last_name: string };
  shippingAddress: {
    street_1: string;
    street_2?: string;
    city: string;
    zip: string;
    country: string;
  } | null;
  products: Product[];
  orderAmount: number;
  subtotalAmount?: number;
  shippingAmount?: number;
  taxAmount?: number;
  currency: string;
  exchangeRate?: number;
  wspayData: {
    approvalCode: string;
    amount: number;
    dateTime: string;
    wsPayOrderId: string;
  } | null;
}

const formatDisplayAmount = (amount?: number | string): string => {
  if (amount == null || amount === "") return "";
  const numeric = parseFloat(String(amount).replace(",", "."));
  if (isNaN(numeric)) return "RSD 0,00";
  return `RSD\u00a0${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric)}`;
};

const parseWSPayDate = (dt?: string): string => {
  if (!dt || dt.length < 14) return "-";
  const y = +dt.slice(0, 4),
    mo = +dt.slice(4, 6) - 1,
    d = +dt.slice(6, 8);
  const h = +dt.slice(8, 10),
    mi = +dt.slice(10, 12),
    s = +dt.slice(12, 14);
  return new Date(y, mo, d, h, mi, s).toLocaleString();
};

const CheckoutSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const hash = searchParams.get("hash");

  const region = useSelector((state) => state.location.preferredLocation);
 

  const { data: storeSettings } = wordPressApi.useGetGlobalStoreSettingsQuery();
  const orderConfirmationContent =
    storeSettings?.storeGeneralSettings?.globalStoreSettings?.orderConfirmation?.content;

  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!orderId || !hash) return;

    const isTestEnv = window.location.href.includes("testmona");
    const baseUrl = isTestEnv
      ? "https://api.testmona.online/api"
      : "https://api.monaonline.com/api";

    setIsLoading(true);
    fetch(`${baseUrl}/bc/order/${orderId}?hash=${hash}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch order");
        return res.json();
      })
      .then((json) => {
        setOrderData(json?.data ?? json);
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  }, [orderId, hash]);

  const formatPrice = (amount: number, currency: string) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);

  const renderContent = () => {
    if (!orderId || !hash) {
      return (
        <p>
          {t("order_confirmation.invalid_order_link")}{" "}
          <a href={region ? `/${region}` : "/"}>{t("order_confirmation.return_to_home")}</a>
        </p>
      );
    }

    if (isLoading) return <LoadingSpinner />;

    if (isError || !orderData) {
      return (
        <p>
          {t("order_confirmation.load_error_message")}{" "}
          <a href={region ? `/${region}` : "/"}>{t("order_confirmation.return_to_home")}</a>
        </p>
      );
    }

    const { billingAddress, shippingAddress, products, orderAmount, subtotalAmount, shippingAmount, taxAmount, currency, exchangeRate, wspayData } = orderData;

    const formatWithRsd = (amount: number) => {
      const base = formatPrice(amount, currency);
      const rsd = exchangeRate ? ` / RSD ${(amount * exchangeRate).toFixed(2)}` : "";
      return `${base}${rsd}`;
    };
    const firstName = billingAddress?.first_name;

    return (
      <div className="layout optimizedCheckout-contentPrimary">
        <div className="layout-main">
          <div className="orderConfirmation">

            <h1 className="optimizedCheckout-headingPrimary" data-test="order-confirmation-heading">
              {firstName
                ? t("order_confirmation.thank_you_customer_heading", { name: firstName })
                : t("order_confirmation.thank_you_heading")}
            </h1>

            {wspayData && (
              <p style={{ marginBottom: 24, fontSize: 14 }}>
                {t("order_confirmation.wspay_transaction_success")}
              </p>
            )}

            <div className="orderConfirmation-section">
              <p data-test="order-confirmation-order-number-text">
                {t('order_confirmation.order_number_text')} <strong>{orderData.orderId} Radan</strong>
              </p>

              {shippingAddress && (
                <>
                  <p style={{ marginTop: 16 }}>{t('order_confirmation.delivery_address')}</p>
                  <p className="orderConfirmation-address">
                    <span>{shippingAddress.street_1}{shippingAddress.street_2 ? ` / ${shippingAddress.street_2}` : ""}</span>
                    <span>{shippingAddress.city} / {shippingAddress.zip} / {shippingAddress.country}</span>
                  </p>
                </>
              )}

              {wspayData && (
                <p style={{ marginTop: 16, fontSize: 14, lineHeight: 1.8 }}>
                  <strong>{t("order_confirmation.wspay_transaction_info_heading")}</strong><br />
                  {t("order_confirmation.wspay_date_time_label")} {parseWSPayDate(wspayData.dateTime)}<br />
                  {t("order_confirmation.wspay_approval_code_label")} {wspayData.approvalCode}<br />
                  {t("order_confirmation.wspay_amount_paid_label")} {formatDisplayAmount(wspayData.amount)}<br />
                  {t("order_confirmation.wspay_order_id_label")} {wspayData.wsPayOrderId}
                </p>
              )}
            </div>

            {orderConfirmationContent && (
              <div
                className="orderConfirmation-wpContent"
                dangerouslySetInnerHTML={{ __html: orderConfirmationContent }}
              />
            )}

            <div className="continueButtonContainer">
              <a
                href={region ? `/${region}` : "/"}
                data-test="order-confirmation-continue"
                id="order-confirmation-continue"
                target="_top"
              >
                {t("order.confirmation_continue_shopping")}
              </a>
            </div>

          </div>
        </div>

        <aside className="layout-cart">
          <article className="cart optimizedCheckout-orderSummary" data-test="cart">

            <header className="cart-header">
              <h3 className="cart-title optimizedCheckout-headingSecondary">
                {t('cart.cart_heading')}
              </h3>
            </header>

            <section className="cart-section optimizedCheckout-orderSummary-cartSection">
              <ul className="productList">
                {products.map((p, i) => (
                  <li key={i} className="productList-item">
                    <div className="product" data-test="cart-item">
                      <figure className="product-column product-figure">
                        {p.image_url
                          ? <img src={p.image_url} alt={p.name} />
                          : <div style={{ width: '100%', height: '112px', background: '#f5f5f5' }} />
                        }
                      </figure>
                      <div className="product-column product-body">
                        <h4 className="product-title optimizedCheckout-contentPrimary" data-test="cart-item-product-title">
                          {p.quantity} x {p.name}
                        </h4>
                        {p.product_options && p.product_options.length > 0 && (
                          <ul className="product-options optimizedCheckout-contentSecondary" data-test="cart-item-product-options">
                            {p.product_options.map((opt, j) => (
                              <li className="product-option" key={j}>
                                {opt.display_name}: {opt.display_value}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="product-price--wrapper">
                          <div className="product-price optimizedCheckout-contentPrimary" data-test="cart-item-product-price">
                            {formatWithRsd(parseFloat(String(p.price_inc_tax)))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section className="cart-section optimizedCheckout-orderSummary-cartSection">
              {subtotalAmount != null && (
                <div className="cart-priceItem cart-priceItem--subtotal optimizedCheckout-contentPrimary" data-test="cart-subtotal">
                  <span className="cart-priceItem-label">
                    <span data-test="cart-price-label">{t('cart.subtotal_text')}: </span>
                  </span>
                  <span className="cart-priceItem-value">
                    <span data-test="cart-price-value">{formatWithRsd(subtotalAmount)}</span>
                  </span>
                </div>
              )}
              {shippingAmount != null && (
                <div className="cart-priceItem optimizedCheckout-contentPrimary" data-test="cart-shipping">
                  <span className="cart-priceItem-label">
                    <span data-test="cart-price-label">{t('cart.shipping_text')}: </span>
                  </span>
                  <span className="cart-priceItem-value">
                    <span data-test="cart-price-value">
                      {shippingAmount === 0 ? t('cart.free_text') : formatWithRsd(shippingAmount)}
                    </span>
                  </span>
                </div>
              )}
              {taxAmount != null && (
                <div className="cart-priceItem optimizedCheckout-contentPrimary" data-test="cart-taxes">
                  <span className="cart-priceItem-label">
                    <span data-test="cart-price-label">{t('cart.taxes_text')}: </span>
                  </span>
                  <span className="cart-priceItem-value">
                    <span data-test="cart-price-value">{formatWithRsd(taxAmount)}</span>
                  </span>
                </div>
              )}
            </section>

            <section className="cart-section optimizedCheckout-orderSummary-cartSection">
              <div className="cart-priceItem cart-priceItem--total optimizedCheckout-contentPrimary" data-test="cart-total">
                <span className="cart-priceItem-label">
                  <span data-test="cart-price-label">{t('cart.total_text')} ({currency}): </span>
                </span>
                <span className="cart-priceItem-value">
                  <span data-test="cart-price-value">{formatWithRsd(orderAmount)}</span>
                </span>
              </div>
            </section>

          </article>
        </aside>
      </div>
    );
  };

  return (
    <IonContent id="ion-content" style={{ "--background": "#fff", "--color": "#333" }}>
      <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", background: "#fff" }}>
        <div style={{ flex: 1 }}>
          {renderContent()}
        </div>
        <Footer />
      </div>
    </IonContent>
  );
};

export default CheckoutSuccessPage;
